import React, { useMemo } from "react";
import Select, { components } from "react-select";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

/**
 * Custom ValueContainer to limit displayed values in multi-select mode
 * Shows only first 2 items and "..." for remaining items
 */
const CustomValueContainer = React.memo(({ children, ...props }) => {
  const selectedValues = props.selectProps.value || [];
  const maxItemsToShow = props.selectProps.maxItemsToShow || 2;
  const tooltipId = props.selectProps.tooltipId || "multi-select-tooltip";
  const ellipsisStyle = props.selectProps.ellipsisStyle || {};
  const tooltipStyle = props.selectProps.tooltipStyle || {};
  const showEllipsis = props.selectProps.showEllipsis ?? false;

  // Memoize allItemNames to prevent unnecessary recalculations (must be before early returns)
  const allItemNames = React.useMemo(
    () => selectedValues.map((item) => item.label).join(", "),
    [selectedValues]
  );

  // If showEllipsis is false, display all options normally
  if (!showEllipsis) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  // For multi-select with 2 or fewer items, show all normally
  if (selectedValues.length <= maxItemsToShow) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  // If more than maxItemsToShow items, filter children to only show first N MultiValues
  const childrenArray = React.Children.toArray(children);

  // The first N children are MultiValues (where N = selectedValues.length)
  // The remaining children are typically input/placeholder elements
  const multiValueCount = selectedValues.length;
  const firstNMultiValues = childrenArray.slice(0, maxItemsToShow);
  const inputElement = childrenArray.slice(multiValueCount);

  // Default ellipsis styles - matching default react-select MultiValue grey color
  const defaultEllipsisStyle = {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "#e6e6e6",
    borderRadius: "2px",
    padding: "2px 8px",
    color: "#212721",
    border: "1px solid #e6e6e6",
    maxHeight: "30px",
    cursor: "pointer",
    ...ellipsisStyle,
  };

  // Create a "..." element styled like react-select's MultiValue
  const ellipsisElement = (
    <div
      key="ellipsis"
      data-tooltip-id={tooltipId}
      data-tooltip-content={allItemNames}
      style={defaultEllipsisStyle}
    >
      <span style={{ paddingBottom: "10px" }}>...</span>
    </div>
  );

  // Default tooltip styles
  const defaultTooltipStyle = {
    backgroundColor: "#333",
    color: "#fff",
    padding: "8px 12px",
    borderRadius: "4px",
    fontSize: "14px",
    maxWidth: "300px",
    zIndex: 99999,
    ...tooltipStyle,
  };

  return (
    <>
      <components.ValueContainer {...props}>
        {firstNMultiValues}
        {ellipsisElement}
        {inputElement}
      </components.ValueContainer>
      <Tooltip id={tooltipId} place="top" style={defaultTooltipStyle} />
    </>
  );
});

CustomValueContainer.displayName = "CustomValueContainer";

/**
 * Custom MultiValue component - passes through since ValueContainer handles filtering
 */
const CustomMultiValue = React.memo((props) => {
  return <components.MultiValue {...props} />;
});

CustomMultiValue.displayName = "CustomMultiValue";

/**
 * Enhanced Select Component with multi-select ellipsis functionality
 * 
 * @param {Object} props - Component props
 * @param {Array} props.options - Array of option objects with label and value
 * @param {Array} props.value - Selected value(s). Array for multi-select
 * @param {Function} props.onChange - Callback function when selection changes
 * @param {boolean} props.isDisabled - Whether the select is disabled
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.styles - Custom styles object for react-select
 * @param {Object} props.theme - Custom theme object for react-select
 * @param {number} props.maxItemsToShow - Maximum items to show before ellipsis in multi-select (default: 2)
 * @param {string} props.tooltipId - Unique ID for tooltip (default: "multi-select-tooltip")
 * @param {Object} props.ellipsisStyle - Custom styles for ellipsis element
 * @param {Object} props.tooltipStyle - Custom styles for tooltip
 * @param {boolean} props.showEllipsis - Whether to enable ellipsis behavior (default: false)
 * @param {boolean} props.isClearable - Whether to show clear button
 * @param {boolean} props.isSearchable - Whether the select is searchable
 * @param {Object} props.components - Additional custom components to override
 * @param {string} props.className - Additional CSS class name
 * @param {Object} props.selectProps - Additional props to pass to react-select
 * @returns {JSX.Element} Enhanced Select component
 */
const MultiSelectNew = React.memo(
  ({
    options = [],
    value,
    onChange,
    isDisabled = false,
    placeholder = "Select...",
    styles = {},
    theme = {},
    maxItemsToShow = 2,
    tooltipId = "multi-select-tooltip",
    ellipsisStyle = {},
    tooltipStyle = {},
    showEllipsis = false,
    isClearable = false,
    isSearchable = true,
    components: customComponents = {},
    className = "",
    ...selectProps
  }) => {
    // Default theme colors
    const primaryColor = theme?.colors?.primary || "#CB998D";
    const primary25Color = theme?.colors?.primary25 || "rgba(203,153,141,.15)";

    // Memoize default theme to prevent unnecessary re-renders
    const defaultTheme = useMemo(
      () => ({
        colors: {
          primary: primaryColor,
          primary25: primary25Color,
        },
        ...theme,
      }),
      [theme, primaryColor, primary25Color]
    );

    // Memoize default styles
    const defaultStyles = useMemo(
      () => ({
        control: (baseStyles, state) => ({
          ...baseStyles,
          borderTop: "none",
          borderRight: "none",
          borderLeft: "none",
          // borderBottomWidth: "1px",
          borderBottomWidth: "1px",
          borderBottomColor: "#cccccc",
          borderBottomStyle: "solid",
          borderRadius: 0,
          background: "transparent",
          boxShadow: "none",
          fontSize: 20,
          // Use box-shadow to create thicker border effect on focus without layout shift
          ...(state.isFocused && {
            boxShadow: "0 1px 0 0 #cccccc",
          }),
        }),
        dropdownIndicator: (baseStyles) => ({
          ...baseStyles,
          color: "#cccccc",
          ":hover": {
            color: "#cccccc",
          },
        }),
        singleValue: (baseStyles) => ({
          ...baseStyles,
          color: "#212721",
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          paddingLeft: 0,
          color: "#212721",
        }),
        placeholder: (baseStyles) => ({
          ...baseStyles,
          color: "#808080",
        }),
        multiValue: (baseStyles) => ({
          ...baseStyles,
          backgroundColor: "#e6e6e6",
        }),
        multiValueLabel: (baseStyles) => ({
          ...baseStyles,
          color: "#212721",
        }),
        multiValueRemove: (baseStyles) => ({
          ...baseStyles,
          color: "#757575",
          ":hover": {
            backgroundColor: "#d4d4d4",
            color: "#212721",
          },
        }),
        option: (provided, state) => ({
          ...provided,
          backgroundColor: state.isSelected
            ? primaryColor
            : state.isFocused
            ? primary25Color
            : "#fff",
          color: state.isSelected ? "#fff" : "#212721",
          opacity: 1,
          ":hover": {
            backgroundColor: state.isSelected ? "#b88a7e" : primary25Color,
            color: state.isSelected ? "#fff" : "#212721",
            opacity: 1,
          },
        }),
        menu: (provided) => ({
          ...provided,
          backgroundColor: "#fff",
          zIndex: 9999,
        }),
        menuList: (provided) => ({
          ...provided,
          backgroundColor: "#fff",
          padding: 0,
        }),
        ...styles,
      }),
      [styles, primaryColor, primary25Color]
    );

    // Memoize components to prevent unnecessary re-renders
    const mergedComponents = useMemo(
      () => ({
        IndicatorSeparator: () => null,
        MultiValue: CustomMultiValue,
        ValueContainer: CustomValueContainer,
        ...customComponents,
      }),
      [customComponents]
    );

    // Memoize select props to pass to custom components
    const enhancedSelectProps = useMemo(
      () => ({
        isMulti: true,
        maxItemsToShow,
        tooltipId,
        ellipsisStyle,
        tooltipStyle,
        showEllipsis,
        ...selectProps,
      }),
      [maxItemsToShow, tooltipId, ellipsisStyle, tooltipStyle, showEllipsis, selectProps]
    );

    return (
      <Select
        options={options}
        value={value}
        onChange={onChange}
        isMulti={true}
        isDisabled={isDisabled}
        placeholder={placeholder}
        styles={defaultStyles}
        theme={defaultTheme}
        isClearable={isClearable}
        isSearchable={isSearchable}
        components={mergedComponents}
        className={className}
        {...enhancedSelectProps}
      />
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Only re-render if these props change
    return (
      prevProps.value === nextProps.value &&
      prevProps.options === nextProps.options &&
      prevProps.isDisabled === nextProps.isDisabled &&
      prevProps.placeholder === nextProps.placeholder &&
      prevProps.maxItemsToShow === nextProps.maxItemsToShow &&
      prevProps.tooltipId === nextProps.tooltipId &&
      prevProps.showEllipsis === nextProps.showEllipsis
    );
  }
);

MultiSelectNew.displayName = "MultiSelectNew";

export default MultiSelectNew;

